import { useEffect, useState, type FormEvent } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase.js';
import { AssignPanel } from './components/AssignPanel.js';
import { AdherencePanel } from './components/AdherencePanel.js';

/**
 * M0 clinician shell: a working email/password login and an authenticated empty
 * home. This is the "log in to an empty clinician app" the M0 done-criteria call for.
 * Real screens (builder, roster, assign, export) arrive in M2+.
 */
export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  if (loading) {
    return <div className="grid min-h-dvh place-items-center text-slate-500">Loading…</div>;
  }

  return session ? <Home email={session.user.email ?? ''} /> : <Login />;
}

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setBusy(false);
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-slate-50 p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 shadow-sm"
      >
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Nostos</h1>
          <p className="text-sm text-slate-500">Clinician sign in</p>
        </div>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-lg bg-slate-900 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

function Home({ email }: { email: string }) {
  // Bumping this after an assign re-pulls adherence (which now includes the new athlete).
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="min-h-dvh bg-slate-50 p-6">
      <header className="mx-auto flex max-w-md items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Nostos</h1>
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span className="hidden sm:inline">{email}</span>
          <button
            onClick={() => supabase.auth.signOut()}
            className="rounded-lg border border-slate-300 px-3 py-1.5 font-medium text-slate-700"
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="mx-auto mt-6 max-w-md space-y-6">
        <AssignPanel onAssigned={() => setRefreshKey((k) => k + 1)} />
        <AdherencePanel refreshKey={refreshKey} />
      </main>
    </div>
  );
}
